import {Document, Font, Page, Text, StyleSheet, View} from '@react-pdf/renderer'
import {Order} from '@cypro/engine/schema'
import {getDatetimeFormatter, getShortDateFormatterUTC} from '@cypro/ui/utils'
import {AggregatedBatchInfoResult} from '@cypro/engine/utils'
import {DefaultParamType, TFnType, TranslationKey} from '@tolgee/react'
import {getPickingListPrescriptions} from './picking-list/print-prescriptions'
import type {SolventRequirement} from './picking-list/utils'

// The built-in Helvetica has no Polish letters, so the picking list embeds the label's fonts.
// react-pdf renders a variable font at its default instance: Geist is Regular (400), Satoshi is Black (900).
// Medium (500) runs resolve to Geist and semibold (600) runs to Satoshi, as the label's family does.
Font.register({
  family: 'PickingList',
  fonts: [
    {src: '/fonts/Geist-Variable.ttf', fontWeight: 'normal'},
    {src: '/fonts/Satoshi-Variable.ttf', fontWeight: 'bold'},
  ],
})

const styles = StyleSheet.create({
  page: {
    paddingTop: 80,
    paddingBottom: 60,
    paddingHorizontal: 30,
    fontSize: 12,
    fontFamily: 'PickingList',
  },
  header: {
    position: 'absolute',
    top: 20,
    left: 30,
    right: 30,
    fontSize: 12,
    paddingBottom: 10,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    fontSize: 12,
    paddingBottom: 10,
  },
  row: {
    flexDirection: 'row',
    borderBottom: '0.5px solid #E4E4E7',
    padding: '8px',
    // a row grows when its prescription list wraps (CY-411)
    minHeight: '28px',
  },
  cell: {
    flex: 1,
    fontSize: '8px',
    lineHeight: '12px',
    paddingRight: '4px',
  },
  cellHeader: {
    flex: 1,
    fontWeight: 500,
    fontSize: '8px',
    lineHeight: '12px',
    paddingRight: '4px',
    color: '#A1A1AA',
  },
  checkbox: {
    width: 12,
    height: 12,
    border: '1px solid #E4E4E7',
    borderRadius: 2,
    marginRight: 8,
  },
})

// One column set for batches, rest vials and returns, so the picker reads every row the same way (CY-411).
const COLUMN_FLEX = {check: 0.2, qty: 0.25, product: 1.4, batch: 0.9, location: 0.9, expiry: 0.75, prescriptions: 1.1}

function TableHeader({t}: {t: TFnType<DefaultParamType, string, TranslationKey>}) {
  return (
    <View style={styles.row} fixed>
      <View style={[styles.cellHeader, {flex: COLUMN_FLEX.check}]}>
        <View style={styles.checkbox} />
      </View>
      <Text style={[styles.cellHeader, {flex: COLUMN_FLEX.qty}]}>{t('qty')}</Text>
      <Text style={[styles.cellHeader, {flex: COLUMN_FLEX.product}]}>{t('product')}</Text>
      <Text style={[styles.cellHeader, {flex: COLUMN_FLEX.batch}]}>{t('batch_number')}</Text>
      <Text style={[styles.cellHeader, {flex: COLUMN_FLEX.location}]}>{t('storage_location')}</Text>
      <Text style={[styles.cellHeader, {flex: COLUMN_FLEX.expiry}]}>{t('expiration_date')}</Text>
      <Text style={[styles.cellHeader, {flex: COLUMN_FLEX.prescriptions}]}>{t('prescriptions')}</Text>
    </View>
  )
}

function TableRow(props: {
  quantity: string | number
  product: string
  productNote?: string | null
  batch: string | null | undefined
  location: string | null | undefined
  expiry: string
  prescriptions: string[]
}) {
  return (
    <View style={styles.row} wrap={false}>
      <View style={[styles.cell, {flex: COLUMN_FLEX.check}]}>
        <View style={styles.checkbox} />
      </View>
      <Text style={[styles.cell, {flex: COLUMN_FLEX.qty}]}>{props.quantity}</Text>
      <View style={[styles.cell, {flex: COLUMN_FLEX.product}]}>
        <Text>{props.product || '-'}</Text>
        {props.productNote && <Text style={{color: '#52525B'}}>{props.productNote}</Text>}
      </View>
      <Text style={[styles.cell, {flex: COLUMN_FLEX.batch}]}>{props.batch || '-'}</Text>
      <Text style={[styles.cell, {flex: COLUMN_FLEX.location}]}>{props.location || '-'}</Text>
      <Text style={[styles.cell, {flex: COLUMN_FLEX.expiry}]}>{props.expiry}</Text>
      <Text style={[styles.cell, {flex: COLUMN_FLEX.prescriptions}]}>{props.prescriptions.join(', ') || '-'}</Text>
    </View>
  )
}

// A batch expiry is a calendar day; a rest vial's or a return's is a moment, so it keeps its time.
const formatDay = (value: number | string | null | undefined) =>
  value ? getShortDateFormatterUTC().format(new Date(value)) : '-'
const formatMoment = (value: number | string | null | undefined) =>
  value ? getDatetimeFormatter().format(new Date(value)) : '-'

const batchTypes = ['activeSubstances', 'carrier', 'solvents', 'consumables']

export const PickingListPrint = ({
  order,
  mapped,
  t,
  solventRequirements,
}: {
  /** the order with its prescriptions, whose numbers are printed on each row */
  order: Order & {sequences?: readonly {id: string; internalId: string | null}[]}
  mapped: AggregatedBatchInfoResult
  t: TFnType<DefaultParamType, string, TranslationKey>
  /** set when the solvent stock check is skipped: solvents print by amount, without batches */
  solventRequirements?: SolventRequirement[]
}) => {
  const solventTotalMl = solventRequirements?.reduce((sum, r) => sum + r.amountMl, 0) ?? 0
  const prescriptions = getPickingListPrescriptions(order.distributedOrders?.orders ?? [], order.sequences ?? [])
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <View style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            <Text style={{fontWeight: 'semibold', fontSize: '14px'}}>{t('picking_list')}</Text>
            <View style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
              <Text style={{fontWeight: 'normal', fontSize: '10px', color: '#52525B'}}>
                {t('generated_date', {
                  date: getDatetimeFormatter().format(order.createdAt || new Date().getTime()),
                })}
              </Text>
              <Text style={{fontWeight: 'normal', fontSize: '10px', color: '#52525B'}}>#{order.internalId}</Text>
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <View style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <Text style={{fontWeight: 'normal', fontSize: '10px', color: '#52525B'}}>
              {order.productionLocation?.name || 'No production location'}
            </Text>
            <Text
              style={{fontWeight: 'normal', fontSize: '10px', color: '#52525B'}}
              render={({pageNumber, totalPages}) => `Page ${pageNumber} of ${totalPages}`}
            />
          </View>
        </View>
        <View
          render={({pageNumber}) =>
            pageNumber === 1 && (
              <View style={{display: 'flex', flexDirection: 'row', gap: '16px'}}>
                {batchTypes.map(type => {
                  const items = mapped.mappedBatches.filter(b => b.type === type)
                  const totalQuantity = items.reduce((sum, row) => sum + row.quantity, 0)
                  const title = t(type)
                  return (
                    <View
                      key={`summary-${type}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        paddingHorizontal: '16px',
                        paddingVertical: '8px',
                        gap: '4px',
                        backgroundColor: '#FAFAFA',
                        width: '100%',
                      }}
                    >
                      <Text style={{fontWeight: 500, fontSize: '10px'}}>
                        {type === 'solvents' && solventRequirements ? `${solventTotalMl} ml` : totalQuantity}
                      </Text>
                      <Text style={{fontWeight: 500, fontSize: '10px', color: '#52525B'}}>{title}</Text>
                    </View>
                  )
                })}
              </View>
            )
          }
        />

        {/* Four tables for each type */}
        {batchTypes.map(type => {
          // Filter batches by type
          const items = mapped.mappedBatches.filter(b => b.type === type)
          if (!items.length) return null
          // Table title
          const title = t(type)
          return (
            <View key={type} style={{marginTop: '16px'}}>
              <Text style={{fontWeight: 500, fontSize: '10px', paddingBottom: '8px', paddingTop: '16px'}} wrap={false}>
                {title}
              </Text>
              <TableHeader t={t} />
              {items.map(item => (
                <TableRow
                  key={`row-${item.batchId}-${item.sharedWith.join(',')}`}
                  quantity={item.quantity}
                  product={
                    (type === 'consumables' ? item.batchInfo?.consumable?.name : item.batchInfo?.tradeName?.name) || '-'
                  }
                  batch={item.batchInfo?.lotNumber}
                  location={item.batchInfo?.stockLocation?.name}
                  expiry={formatDay(item.batchInfo?.expiryDate)}
                  prescriptions={prescriptions.forOrderIds([...item.sharedWith, ...(item.ordersSharingThisBatch ?? [])])}
                />
              ))}
            </View>
          )
        })}

        {solventRequirements && solventRequirements.length > 0 && (
          <View style={{marginTop: '16px'}}>
            <Text style={{fontWeight: 500, fontSize: '10px', paddingBottom: '8px', paddingTop: '16px'}} wrap={false}>
              {`${t('solvents')} · ${t('solvent_no_stock_check')}`}
            </Text>
            <View style={styles.row} fixed>
              <Text style={[styles.cellHeader, {flex: 0.4}]}>{t('qty')}</Text>
              <Text style={[styles.cellHeader]}>{t('product')}</Text>
            </View>
            {solventRequirements.map(requirement => (
              <View style={styles.row} key={`solvent-${requirement.solutionDrugId}`} wrap={false}>
                <Text style={[styles.cell, {flex: 0.4}]}>{`${requirement.amountMl} ml`}</Text>
                <Text style={styles.cell}>{requirement.name}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Rest vials table */}
        {mapped.mappedRestVials.length > 0 && (
          <View style={{marginTop: '16px'}}>
            <Text style={{fontWeight: 500, fontSize: '10px', paddingBottom: '8px', paddingTop: '16px'}} wrap={false}>
              {t('rest_vials')}
            </Text>
            <TableHeader t={t} />
            {mapped.mappedRestVials.map(item => (
              <TableRow
                key={`row-${item.restVialId}`}
                quantity={1}
                product={item.batchInfo?.consumable?.name || item.batchInfo?.tradeName?.name || '-'}
                batch={item.batchInfo?.lotNumber}
                location={item.batchInfo?.stockLocation?.name}
                expiry={formatMoment(item.expiryDate)}
                prescriptions={prescriptions.forOrderIds(item.ordersSharingThisBatch)}
              />
            ))}
          </View>
        )}

        {/* Pharmacy returns table */}
        {mapped.mappedReturns.length > 0 && (
          <View style={{marginTop: '16px'}}>
            <Text style={{fontWeight: 500, fontSize: '10px', paddingBottom: '8px', paddingTop: '16px'}} wrap={false}>
              {t('pharmacy_return')}
            </Text>
            <TableHeader t={t} />
            {mapped.mappedReturns.map(ret => (
              <TableRow
                key={`return-${ret.returnId}`}
                quantity={1}
                product={ret.medicationName || '-'}
                productNote={ret.returnedAt ? `${t('returned_at')}: ${formatMoment(ret.returnedAt)}` : null}
                batch={null}
                location={ret.stockLocationName}
                expiry={formatMoment(ret.expiryDate)}
                prescriptions={prescriptions.forReturn(ret.returnId)}
              />
            ))}
          </View>
        )}
      </Page>
    </Document>
  )
}
